import { FC, ReactNode } from 'react';
import { BrandLogo } from '@kontentino/ui';
import getSocialMediaTypeForBrandLogo from 'app/utils/getSocialMediaTypeForBrandLogo';
import { getYtLogoGuidelineSize } from 'app/utils/getytlogoguidelinesize';
import { useTranslation } from 'react-i18next';
import {
  analyticsExportPageShellClassName,
  getAnalyticsExportPageStyle,
} from './constants/analyticsExportPageLayout';
import { useAnalyticsExportTheme } from './analyticsExportTheme';

export interface AnalyticsExportPageProfile {
  name: string;
  imageUrl?: string;
  pageType?: number;
}

interface Props {
  title: string;
  subtitle: string;
  profile: AnalyticsExportPageProfile;
  pageNumber: number;
  totalPages: number;
  dataCy: string;
  children: ReactNode;
}

/** One A4 landscape sheet: title block, the body it is given, and the footer. */
const AnalyticsExportPageFrame: FC<Props> = ({
  title,
  subtitle,
  profile,
  pageNumber,
  totalPages,
  dataCy,
  children,
}) => {
  const { t } = useTranslation();
  const theme = useAnalyticsExportTheme();

  return (
    <article
      className={`${analyticsExportPageShellClassName} ${theme.page}`}
      style={getAnalyticsExportPageStyle(pageNumber, totalPages)}
      data-cy={dataCy}
    >
      <header className="tw-flex tw-items-start tw-gap-6">
        <div className="tw-flex tw-flex-col tw-gap-2">
          <h1
            className={`tw-text-4xl tw-font-bold ${theme.title}`}
            translate="no"
          >
            {title}
          </h1>
          <p className={`tw-text-lg ${theme.subtitle}`}>{subtitle}</p>
        </div>
        {profile.pageType != null ? (
          <BrandLogo
            className="tw-ml-auto tw-shrink-0"
            size={56}
            iconSize={getYtLogoGuidelineSize(20, profile.pageType)}
            name={profile.name}
            src={profile.imageUrl}
            socialMediaType={getSocialMediaTypeForBrandLogo(profile.pageType)}
          />
        ) : null}
      </header>
      {children}
      {/* `mt-auto` keeps the footer on the bottom edge even if a page body
          does not grow. */}
      <footer
        className={`tw-mt-auto tw-flex tw-items-center tw-justify-end tw-text-sm ${theme.footer}`}
      >
        <p>
          {t('analyticsReports.createReport.previewGeneratedBy')}{' '}
          <a
            href="https://kontentino.com"
            target="_blank"
            rel="noopener noreferrer"
            className="tw-underline"
          >
            Kontentino
          </a>
        </p>
      </footer>
    </article>
  );
};

export default AnalyticsExportPageFrame;
